import type { GoalContributionResponse, GoalResponse, PaceStatus } from '@/api/goals'
import { fromPaise, toPaise } from '@/lib/splitMath'

const ATTENTION: PaceStatus[] = ['OVERDUE', 'BEHIND', 'NEW', 'ON_TRACK', 'NO_DATE', 'REACHED']

function attentionRank(g: GoalResponse): number {
  if (g.earmarkShortfall > 0) return -1
  const i = g.paceStatus ? ATTENTION.indexOf(g.paceStatus) : -1
  return i === -1 ? ATTENTION.length : i
}

/** A short account first, then overdue, behind… down to reached; nearer target dates first within each. */
export function sortNeedsAttentionFirst(goals: GoalResponse[]): GoalResponse[] {
  return [...goals].sort(
    (a, b) =>
      attentionRank(a) - attentionRank(b) ||
      (a.targetDate ?? '9999').localeCompare(b.targetDate ?? '9999') ||
      a.name.localeCompare(b.name)
  )
}

const BADGES: Record<
  PaceStatus,
  { label: string; tone: 'primary' | 'warning' | 'error' | 'neutral' }
> = {
  ON_TRACK: { label: 'On track', tone: 'primary' },
  BEHIND: { label: 'Behind', tone: 'warning' },
  OVERDUE: { label: 'Overdue', tone: 'error' },
  NEW: { label: 'New', tone: 'neutral' },
  NO_DATE: { label: 'No date', tone: 'neutral' },
  REACHED: { label: 'Reached', tone: 'primary' },
}

export function paceBadge(status: PaceStatus | null | undefined) {
  return status ? BADGES[status] : null
}

export function trackedIn(goal: GoalResponse): string {
  return goal.trackingMode === 'EARMARK'
    ? `Set aside in ${goal.linkedAccountName}`
    : `Own account · ${goal.linkedAccountName}`
}

const DAY = 86_400_000

function localMidnight(dateStr: string): number {
  const d = /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
    ? new Date(dateStr + 'T00:00:00')
    : new Date(dateStr)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

/** Where today falls between the goal's start and its target date, 0–1; null without a date. */
export function timeGoneFraction(
  createdAt: string,
  targetDate: string | undefined,
  today: Date
): number | null {
  if (!targetDate) return null
  const start = localMidnight(createdAt)
  const end = localMidnight(targetDate)
  const now = new Date(today)
  now.setHours(0, 0, 0, 0)
  if (end <= start) return 1
  return Math.min(1, Math.max(0, (now.getTime() - start) / (end - start)))
}

export function daysUntil(dateStr: string, today: Date): number {
  const now = new Date(today)
  now.setHours(0, 0, 0, 0)
  return Math.round((localMidnight(dateStr) - now.getTime()) / DAY)
}

export interface ContributionMonth {
  month: string
  net: number
  items: GoalContributionResponse[]
}

/** Newest month first (the API lists newest first); each month's net counts withdrawals as negative. */
export function groupByMonth(rows: GoalContributionResponse[]): ContributionMonth[] {
  const groups: ContributionMonth[] = []
  for (const row of rows) {
    const d = new Date(row.contributedAt)
    const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    let group = groups.find((g) => g.month === month)
    if (!group) {
      group = { month, net: 0, items: [] }
      groups.push(group)
    }
    group.net = fromPaise(toPaise(group.net) + toPaise(row.amount))
    group.items.push(row)
  }
  return groups
}
